import type { ReactNode, MouseEvent, MouseEventHandler } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

interface Props {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  ariaLabel?: string;
  /** in-page scroll animation on home (default true) */
  smooth?: boolean;
}

const HEADER = 56;

/** Scroll to a home section, offset for the fixed header. Pin spacers refreshed first. */
export function scrollToSection(id: string, smooth: boolean) {
  const el = document.getElementById(id);
  if (!el) return false;
  ScrollTrigger.refresh();
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - HEADER, behavior: smooth && !reduce ? 'smooth' : 'auto' });
  return true;
}

/** "/work" → router link. "#id" → in-page on home, "/#id" from sub pages. */
export default function SmartLink({ href, className, children, onClick, ariaLabel, smooth = true }: Props) {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === '/';
  if (href.startsWith('#')) {
    if (isHome) {
      const handle = (e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        const id = href.slice(1);
        if (id === 'top') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
        if (hash !== href) navigate({ hash: href }, { state: { inPage: true } });
        // defer one tick so the mobile menu releases body overflow before scrolling
        window.setTimeout(() => scrollToSection(id, smooth), 30);
      };
      return <a href={href} className={className} onClick={handle} aria-label={ariaLabel}>{children}</a>;
    }
    const to = href === '#top' ? '/' : { pathname: '/', hash: href };
    return <Link to={to} className={className} onClick={onClick} aria-label={ariaLabel}>{children}</Link>;
  }
  return <Link to={href} className={className} onClick={onClick} aria-label={ariaLabel}>{children}</Link>;
}